import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import api from "@/lib/api";
import { gbp, ddmmyyyy } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { CalendarRange, Lock, RotateCcw, PlusCircle, FileText, Loader2, History, Plus, Wand2, Eye } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";

const fetcher = (url) => api.get(url).then((r) => r.data);

const statusStyles = {
  open: "bg-emerald-100 text-emerald-700 border-emerald-200",
  closed: "bg-slate-100 text-slate-700 border-slate-200",
  paid: "bg-blue-100 text-blue-700 border-blue-200",
};

const PayPeriods = () => {
  const { data, isLoading } = useSWR("/pay-periods", fetcher);
  const { data: current } = useSWR("/pay-periods/current", fetcher);
  const [busy, setBusy] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ name: "", start_date: "", end_date: "", status: "closed" });

  const submitManual = async (e) => {
    e.preventDefault();
    if (!manualForm.start_date || !manualForm.end_date) return toast.error("Start and end dates required");
    setBusy("manual");
    try {
      const { data } = await api.post("/pay-periods/manual", manualForm);
      toast.success(`Created '${data.name}'`);
      setManualOpen(false);
      setManualForm({ name: "", start_date: "", end_date: "", status: "closed" });
      mutate("/pay-periods");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    }
    setBusy(null);
  };

  const backfill = async () => {
    setBusy("backfill");
    try {
      const { data } = await api.post("/pay-periods/backfill?months=6");
      toast.success(`Backfilled ${data.created} historical period(s)`);
      mutate("/pay-periods");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    }
    setBusy(null);
  };

  const consolidate = async () => {
    if (!window.confirm("Fix pay periods: exactly ONE period will be OPEN (the one that contains today). All other periods will be CLOSED. Proceed?")) return;
    setBusy("consolidate");
    try {
      const { data } = await api.post("/admin/maintenance/consolidate-pay-periods");
      toast.success(`Current period is now: ${data.current_period.name}. ${data.closed} other period(s) closed.`);
      mutate("/pay-periods");
      mutate("/pay-periods/current");
      mutate("/dashboard/kpis");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Consolidate failed");
    }
    setBusy(null);
  };

  const fixFutureDates = async () => {
    if (!window.confirm("Scan all work rows and repair any that landed in the future (usually a DD/MM vs MM/DD swap). Rows that can't be repaired will be DELETED. Continue?")) return;
    setBusy("fixdates");
    try {
      const { data } = await api.post("/admin/maintenance/fix-future-work-dates");
      toast.success(`Fixed ${data.fixed} row(s). Deleted ${data.deleted}. Kept ${data.kept}.`);
      // After fix, reshard so rows land in the right period
      const { data: rs } = await api.post("/admin/maintenance/reshard-work-rows");
      toast.info(`Re-sharded ${rs.moved} row(s) into correct periods.`);
      mutate("/pay-periods");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fix failed");
    }
    setBusy(null);
  };

  const doAction = async (id, action, label) => {
    setBusy(`${id}:${action}`);
    try {
      await api.post(`/pay-periods/${id}/${action}`);
      toast.success(`${label} complete`);
      mutate("/pay-periods");
      mutate("/dashboard/kpis");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
    setBusy(null);
  };

  const generateInvoices = async (id) => {
    setBusy(`${id}:invoices`);
    try {
      const { data } = await api.post(`/invoices/generate?period_id=${id}`);
      toast.success(`Generated ${data.count} invoice(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generate failed");
    }
    setBusy(null);
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Payroll cycle · DPD-aligned</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-text mt-1">Pay Periods</h1>
          <p className="text-sm text-brand-text-muted mt-1">
            Four-week periods that match DPD&apos;s payment calendar (Mon → Sun). Anchored to 5th Jan 2026.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={consolidate} disabled={busy === "consolidate"} variant="outline"
                  className="border-brand-navy text-brand-navy hover:bg-brand-navy/10"
                  data-testid="consolidate-btn">
            {busy === "consolidate" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1.5" />}
            Fix current period
          </Button>
          <Button onClick={fixFutureDates} disabled={busy === "fixdates"} variant="outline"
                  className="border-red-400 text-red-700 hover:bg-red-50"
                  data-testid="fix-dates-btn">
            {busy === "fixdates" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1.5" />}
            Fix future work dates
          </Button>
          <Button onClick={backfill} disabled={busy === "backfill"} variant="outline" data-testid="backfill-btn">
            {busy === "backfill" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <History className="w-4 h-4 mr-1.5" />}
            Backfill history
          </Button>
          <Dialog open={manualOpen} onOpenChange={setManualOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-red hover:bg-brand-red-hover" data-testid="manual-period-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add period
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add a historical / custom period</DialogTitle>
                <DialogDescription>
                  Use explicit start &amp; end dates — great for onboarding legacy DPD data or bespoke closing periods.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submitManual} className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Name (optional)</Label>
                  <Input value={manualForm.name} data-testid="manual-name"
                    onChange={(e) => setManualForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Auto-generated from dates if empty" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Start date *</Label>
                    <Input type="date" required value={manualForm.start_date} data-testid="manual-start"
                      onChange={(e) => setManualForm((f) => ({ ...f, start_date: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-brand-text-muted">End date *</Label>
                    <Input type="date" required value={manualForm.end_date} data-testid="manual-end"
                      onChange={(e) => setManualForm((f) => ({ ...f, end_date: e.target.value }))} />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-brand-red hover:bg-brand-red-hover" disabled={busy === "manual"} data-testid="manual-save">
                    {busy === "manual" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
                    Create period
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {current && (
        <Card className="p-5 rounded-2xl border-brand-border bg-brand-navy text-white flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="current-period-card">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">DPD current period</div>
            <div className="font-heading text-2xl font-bold tracking-tight mt-1">{current.name}</div>
            <div className="text-xs text-white/60 mt-1">
              {ddmmyyyy(current.start_date)} → {ddmmyyyy(current.end_date)} · Pays {ddmmyyyy(current.payment_date)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-white/60">Days remaining</div>
            <div className="font-heading text-4xl font-bold tracking-tight">{current.days_remaining}</div>
          </div>
        </Card>
      )}

      {isLoading && <div className="text-sm text-slate-500">Loading…</div>}

      <div className="space-y-3" data-testid="pay-periods-list">
        {(data || []).map((p) => (
          <Card key={p.id} className="p-5 rounded-2xl border-brand-border" data-testid={`period-${p.id}`}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-slate-100 grid place-items-center text-slate-600">
                  <CalendarRange className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-heading font-bold text-brand-text">{p.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {ddmmyyyy(p.start_date)} — {ddmmyyyy(p.end_date)} · Pays {ddmmyyyy(p.payment_date)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={`uppercase tracking-wider text-[10px] ${statusStyles[p.status] || ""}`}>
                  {p.status}
                </Badge>
                <Link to={`/admin/pay-periods/${p.id}`}>
                  <Button size="sm" variant="outline" data-testid={`view-period-${p.id}`}>
                    <Eye className="w-3.5 h-3.5 mr-1.5" /> View
                  </Button>
                </Link>
                <Link to="/admin/invoices">
                  <Button size="sm" variant="outline" data-testid={`view-invoices-${p.id}`}>
                    <FileText className="w-3.5 h-3.5 mr-1.5" /> Invoices
                  </Button>
                </Link>
                <Button
                  size="sm" variant="outline" data-testid={`generate-invoices-${p.id}`}
                  onClick={() => generateInvoices(p.id)} disabled={busy === `${p.id}:invoices`}
                >
                  {busy === `${p.id}:invoices` ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
                  Generate invoices
                </Button>
                {p.status === "open" && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" className="bg-brand-red hover:bg-brand-red-hover" data-testid={`close-period-${p.id}`}>
                        <Lock className="w-3.5 h-3.5 mr-1.5" /> Close & create next
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Close period {p.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Closing locks this period from further edits and creates the next 4-week period automatically.
                          You can still generate/regenerate invoices for it. Reopen requires admin action.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-brand-red hover:bg-brand-red-hover"
                          onClick={() => doAction(p.id, "create-next", "Close & create next")}
                        >
                          Close & create next
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {p.status === "closed" && (
                  <Button
                    size="sm" variant="outline" data-testid={`reopen-period-${p.id}`}
                    onClick={() => doAction(p.id, "reopen", "Reopen")} disabled={busy === `${p.id}:reopen`}
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reopen
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
        {!isLoading && (data || []).length === 0 && (
          <Card className="p-12 text-center text-sm text-slate-500 rounded-2xl">
            <PlusCircle className="w-6 h-6 mx-auto mb-2 opacity-40" />
            No pay periods yet.
          </Card>
        )}
      </div>
    </div>
  );
};

export default PayPeriods;
